import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import UpgradeModal from '@/components/feature/UpgradeModal';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import Logo from '@/components/feature/Logo';

const SCROLL_KEY = 'admin-sidebar-scroll-v1';
const EXPANDED_KEY = 'admin-sidebar-expanded-v1';
const SCROLL_DELAY_MS = 50;

interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  locked?: boolean;
  lockFeatureId?: string;
  children?: { id: string; label: string; icon: string; path: string; lockFeatureId?: string }[];
}

export const navItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line', path: '/admin/dashboard' },
  {
    id: 'cabina',
    label: 'Cabina',
    icon: 'ri-broadcast-line',
    path: '/admin/cabina',
    children: [
      { id: 'cabina-inbox', label: 'Inbox', icon: 'ri-chat-3-line', path: '/admin/cabina' },
      { id: 'cabina-queue', label: 'Queue', icon: 'ri-list-check', path: '/admin/cabina/queue' },
      { id: 'cabina-saludos', label: 'Saludos', icon: 'ri-user-heart-line', path: '/admin/greetings' },
      { id: 'cabina-canciones', label: 'Canciones', icon: 'ri-music-2-line', path: '/admin/song-requests' },
      { id: 'cabina-moderacion', label: 'Moderación', icon: 'ri-shield-line', path: '/admin/cabina/moderacion' },
      { id: 'cabina-config', label: 'Configuración', icon: 'ri-settings-3-line', path: '/admin/settings' },
    ],
  },
  { id: 'live-chat', label: 'Chat Unificado', icon: 'ri-chat-3-line', path: '/admin/live-chat' },
  { id: 'chat-comunidad', label: 'Chat Comunidad', icon: 'ri-chat-smile-2-line', path: '/admin/chat' },
  { id: 'studio', label: 'Studio', icon: 'ri-broadcast-line', path: '/admin/studio' },
  {
    id: 'programacion',
    label: 'Programación',
    icon: 'ri-calendar-2-line',
    path: '/admin/schedule',
    children: [
      { id: 'programacion-grilla', label: 'Grilla', icon: 'ri-calendar-2-line', path: '/admin/schedule' },
      { id: 'programacion-fichas', label: 'Fichas', icon: 'ri-mic-line', path: '/admin/programs' },
    ],
  },
  {
    id: 'equipo',
    label: 'Equipo',
    icon: 'ri-team-line',
    path: '/admin/staff',
    children: [
      { id: 'equipo-locutores', label: 'Locutores', icon: 'ri-mic-line', path: '/admin/hosts' },
      { id: 'equipo-staff', label: 'Staff & Permisos', icon: 'ri-team-line', path: '/admin/staff' },
    ],
  },
  {
    id: 'contenido',
    label: 'Contenido',
    icon: 'ri-file-list-3-line',
    path: '/admin/posts',
    children: [
      { id: 'contenido-posts', label: 'Publicaciones', icon: 'ri-file-list-3-line', path: '/admin/posts' },
      { id: 'contenido-fotos', label: 'Fotos', icon: 'ri-image-line', path: '/admin/photos', lockFeatureId: 'fotos' },
      { id: 'contenido-videos', label: 'Videos', icon: 'ri-video-line', path: '/admin/videos', lockFeatureId: 'videos' },
      { id: 'contenido-lives', label: 'Lives', icon: 'ri-live-line', path: '/admin/live', lockFeatureId: 'lives' },
    ],
  },
  {
    id: 'eventos-concursos',
    label: 'Eventos & Concursos',
    icon: 'ri-calendar-event-line',
    path: '/admin/events',
    children: [
      { id: 'eventos', label: 'Eventos', icon: 'ri-calendar-event-line', path: '/admin/events' },
      { id: 'concursos', label: 'Concursos', icon: 'ri-trophy-line', path: '/admin/contests' },
    ],
  },
  {
    id: 'monetizacion',
    label: 'Monetización',
    icon: 'ri-money-dollar-circle-line',
    path: '/admin/monetization',
    children: [
      { id: 'monetizacion-panel', label: 'Panel', icon: 'ri-money-dollar-circle-line', path: '/admin/monetization' },
      { id: 'monetizacion-own-ads', label: 'Publicidad Propia', icon: 'ri-advertisement-line', path: '/admin/own-ads' },
      { id: 'monetizacion-rfw', label: 'Red RFW', icon: 'ri-global-line', path: '/admin/rfw-network' },
      { id: 'monetizacion-marketplace', label: 'Advertiser Prospecting', icon: 'ri-user-search-line', path: '/admin/marketplace' },
    ],
  },
  { id: 'comunidad', label: 'Comunidad', icon: 'ri-group-line', path: '/admin/community' },
  { id: 'analytics', label: 'Analytics', icon: 'ri-bar-chart-2-line', path: '/admin/analytics' },
  { id: 'notifications', label: 'Notificaciones', icon: 'ri-notification-3-line', path: '/admin/notifications' },
  { id: 'settings', label: 'Configuración', icon: 'ri-settings-3-line', path: '/admin/settings' },
  { id: 'profile', label: 'Perfil Radio', icon: 'ri-user-star-line', path: '/admin/profile' },
  { id: 'social-connect', label: 'Social Connect', icon: 'ri-share-line', path: '/admin/social-connect' },
  { id: 'support', label: 'Soporte', icon: 'ri-customer-service-2-line', path: '/admin/support' },
  { id: 'knowledge-base', label: 'Ayuda', icon: 'ri-book-open-line', path: '/admin/knowledge-base' },
];

function loadExpandedGroups(): string[] {
  try {
    const raw = sessionStorage.getItem(EXPANDED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : ['cabina'];
  } catch {
    return ['cabina', 'comunidad'];
  }
}

function isElementVisible(container: HTMLElement, child: HTMLElement): boolean {
  const cRect = container.getBoundingClientRect();
  const chRect = child.getBoundingClientRect();
  const threshold = 8;
  return chRect.top >= cRect.top + threshold && chRect.bottom <= cRect.bottom - threshold;
}

export default function Sidebar() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(loadExpandedGroups);
  const [upgradeFeatureId, setUpgradeFeatureId] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { canAccess, isFree, getFeature, currentPlan } = usePlanPermissions();
  const { openUpgradeModal } = useUpgradeModal();
  const navRef = useRef<HTMLElement>(null);
  const mobileNavRef = useRef<HTMLElement>(null);
  const prevPathname = useRef(location.pathname);

  /* ─── Notifications count (for badge) ─── */
  const unreadCount = 3;

  /* ─── Derive which group(s) contain the active path ─── */
  const activeGroupIds = useMemo(() => {
    return navItems
      .filter((item) => item.children?.some((c) => location.pathname === c.path || location.pathname.startsWith(c.path + '/')))
      .map((item) => item.id);
  }, [location.pathname]);

  /* ─── Auto-expand group containing active child on navigation ─── */
  useEffect(() => {
    if (activeGroupIds.length > 0) {
      setExpandedGroups((prev) => {
        const missing = activeGroupIds.filter((id) => !prev.includes(id));
        if (missing.length === 0) return prev;
        const next = [...prev, ...missing];
        sessionStorage.setItem(EXPANDED_KEY, JSON.stringify(next));
        return next;
      });
    }
  }, [activeGroupIds]);

  /* ─── Close mobile drawer on path change ─── */
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const saveScroll = useCallback((nav: HTMLElement | null) => {
    if (!nav) return;
    sessionStorage.setItem(SCROLL_KEY, String(nav.scrollTop));
  }, []);

  /* ─── Smart scroll: restore + scroll to active without double-jump ─── */
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const saved = sessionStorage.getItem(SCROLL_KEY);
    const savedTop = saved ? Number(saved) : 0;

    /* Check where active item should be BEFORE restoring */
    const activeLink = nav.querySelector('[data-sidebar-active="true"]') as HTMLElement | null;

    if (!activeLink) {
      nav.scrollTop = savedTop;
      return;
    }

    const navHeight = nav.clientHeight;
    const itemTop = activeLink.offsetTop;
    const itemHeight = activeLink.offsetHeight;

    /* Calculate if active item would be visible at saved position */
    const visibleAtSaved =
      itemTop >= savedTop + 8 &&
      itemTop + itemHeight <= savedTop + navHeight - 8;

    if (visibleAtSaved) {
      /* Active item was visible where we left off — restore cleanly */
      nav.scrollTop = savedTop;
    } else {
      /* Active item would be out of view — scroll directly to center it */
      const target = itemTop - navHeight / 2 + itemHeight / 2;
      nav.scrollTop = Math.max(0, target);
      /* Save this new position */
      setTimeout(() => {
        if (nav) sessionStorage.setItem(SCROLL_KEY, String(nav.scrollTop));
      }, 200);
    }

    prevPathname.current = location.pathname;
  }, [location.pathname]);

  /* ─── Scroll event: debounced save ─── */
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    let timer: ReturnType<typeof setTimeout>;
    const handler = () => {
      clearTimeout(timer);
      timer = setTimeout(() => saveScroll(nav), SCROLL_DELAY_MS);
    };

    nav.addEventListener('scroll', handler, { passive: true });
    return () => {
      nav.removeEventListener('scroll', handler);
      clearTimeout(timer);
    };
  }, [saveScroll]);

  /* ─── Mobile nav scroll save ─── */
  useEffect(() => {
    const nav = mobileNavRef.current;
    if (!nav) return;

    let timer: ReturnType<typeof setTimeout>;
    const handler = () => {
      clearTimeout(timer);
      timer = setTimeout(() => saveScroll(nav), SCROLL_DELAY_MS);
    };

    nav.addEventListener('scroll', handler, { passive: true });
    return () => {
      nav.removeEventListener('scroll', handler);
      clearTimeout(timer);
    };
  }, [saveScroll]);

  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) => {
      const next = prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id];
      sessionStorage.setItem(EXPANDED_KEY, JSON.stringify(next));
      return next;
    });
  };

  const handleLockedClick = (featureId: string) => {
    setUpgradeFeatureId(featureId);
  };

  const closeUpgradeModal = () => {
    setUpgradeFeatureId(null);
  };

  const isParentActive = (item: NavItem): boolean => {
    if (location.pathname === item.path) return true;
    if (item.children) {
      return item.children.some(
        (c) => location.pathname === c.path || location.pathname.startsWith(c.path + '/'),
      );
    }
    return false;
  };

  const isItemLocked = (item: NavItem): boolean => {
    if (!item.lockFeatureId) return false;
    return item.locked !== undefined ? item.locked : !canAccess(item.lockFeatureId);
  };

  /* ─── Render nav list (shared between desktop sidebar & mobile drawer) ─── */
  const renderNavItems = (isMobile: boolean) => (
    <nav
      ref={isMobile ? mobileNavRef : navRef}
      className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-none"
    >
      {navItems.map((item) => {
        const isActive = isParentActive(item);
        const isExpanded = expandedGroups.includes(item.id);

        return (
          <div key={item.id}>
            {item.children ? (
              /* Parent with children */
              <>
                <button
                  onClick={() => !collapsed && toggleGroup(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
                    isActive
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-foreground-500 hover:bg-background-100 hover:text-foreground-700'
                  } ${collapsed && !isMobile ? 'justify-center' : ''}`}
                  title={collapsed && !isMobile ? item.label : undefined}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      isActive
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-background-200 text-foreground-400'
                    }`}
                  >
                    <i className={`${item.icon} text-sm`}></i>
                  </div>
                  {(!collapsed || isMobile) && (
                    <>
                      <span className="text-sm font-medium whitespace-nowrap flex-1 text-left">{item.label}</span>
                      <i
                        className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 text-xs transition-transform`}
                      ></i>
                    </>
                  )}
                </button>
                {(!collapsed || isMobile) && isExpanded && (
                  <div className="ml-5 mt-0.5 space-y-0.5 border-l-2 border-background-200 pl-3">
                    {item.children.map((child) => {
                      const childActive = location.pathname === child.path || location.pathname.startsWith(child.path + '/');
                      const childLocked = child.lockFeatureId ? !canAccess(child.lockFeatureId) : false;
                      const childFeature = child.lockFeatureId ? getFeature(child.lockFeatureId) : null;

                      if (childLocked) {
                        return (
                          <button
                            key={child.id}
                            onClick={() => child.lockFeatureId && handleLockedClick(child.lockFeatureId)}
                            data-sidebar-active={childActive ? 'true' : 'false'}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer opacity-70 ${
                              childActive
                                ? 'bg-secondary-50 text-secondary-600'
                                : 'text-foreground-500 hover:bg-background-100 hover:text-foreground-700'
                            }`}
                          >
                            <i className={`${child.icon} text-[11px]`}></i>
                            {child.label}
                            <span
                              className={`ml-auto px-1.5 py-0.5 rounded-md text-[9px] font-bold whitespace-nowrap ${
                                childFeature?.requiredPlan.id === 'pro'
                                  ? 'bg-primary-100 text-primary-600'
                                  : 'bg-accent-100 text-accent-600'
                              }`}
                            >
                              {childFeature?.badge || 'Bloqueado'}
                            </span>
                          </button>
                        );
                      }

                      return (
                        <Link
                          key={child.id}
                          to={child.path}
                          data-sidebar-active={childActive ? 'true' : 'false'}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                            childActive
                              ? 'bg-secondary-50 text-secondary-600'
                              : 'text-foreground-500 hover:bg-background-100 hover:text-foreground-700'
                          }`}
                        >
                          <i className={`${child.icon} text-[11px]`}></i>
                          {child.label}
                          {childActive && (
                            <div className="ml-auto w-1.5 h-1.5 bg-secondary-500 rounded-full"></div>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              /* Regular item */
              (() => {
                const locked = isItemLocked(item);
                const feature = item.lockFeatureId ? getFeature(item.lockFeatureId) : null;
                const baseClasses = isActive
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-foreground-500 hover:bg-background-100 hover:text-foreground-700';

                if (locked) {
                  return (
                    <button
                      onClick={() => item.lockFeatureId && handleLockedClick(item.lockFeatureId)}
                      data-sidebar-active={isActive ? 'true' : 'false'}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${baseClasses} opacity-70 ${collapsed && !isMobile ? 'justify-center' : ''}`}
                      title={collapsed && !isMobile ? item.label : undefined}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          isActive
                            ? 'bg-primary-100 text-primary-600'
                            : 'bg-background-200 text-foreground-400'
                        }`}
                      >
                        <i className={`${item.icon} text-sm`}></i>
                      </div>
                      {(!collapsed || isMobile) && (
                        <>
                          <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                          <span
                            className={`ml-auto px-1.5 py-0.5 rounded-md text-[9px] font-bold whitespace-nowrap ${
                              feature?.requiredPlan.id === 'pro'
                                ? 'bg-primary-100 text-primary-600'
                                : 'bg-accent-100 text-accent-600'
                            }`}
                          >
                            {feature?.badge || 'Bloqueado'}
                          </span>
                        </>
                      )}
                    </button>
                  );
                }

                return (
                  <Link
                    to={item.path}
                    data-sidebar-active={isActive ? 'true' : 'false'}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${baseClasses} ${collapsed && !isMobile ? 'justify-center' : ''}`}
                    title={collapsed && !isMobile ? item.label : undefined}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        isActive
                          ? 'bg-primary-100 text-primary-600'
                          : 'bg-background-200 text-foreground-400'
                      }`}
                    >
                      <i className={`${item.icon} text-sm`}></i>
                    </div>
                    {(!collapsed || isMobile) && (
                      <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                    )}
                    {isActive && (!collapsed || isMobile) && (
                      <div className="ml-auto w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                    )}
                  </Link>
                );
              })()
            )}
          </div>
        );
      })}
    </nav>
  );

  /* ─── Nav header (brand + global actions) ─── */
  const renderHeader = (isMobile: boolean) => (
    <div className="flex items-center gap-3 px-5 h-16 border-b border-background-200 shrink-0">
      {isMobile ? (
        <Link to="/admin/dashboard" className="flex items-center gap-3 cursor-pointer min-w-0" onClick={() => setMobileOpen(false)}>
          <Logo size="sm" />
          <div className="min-w-0 overflow-hidden">
            <h1 className="text-foreground-950 font-bold text-sm leading-tight truncate">
              Radio Admin
            </h1>
            <p className="text-foreground-400 text-[10px]">Panel de control</p>
          </div>
        </Link>
      ) : (
        <>
          <Logo size="sm" />
          {!collapsed && (
            <div className="min-w-0 overflow-hidden">
              <h1 className="text-foreground-950 font-bold text-sm leading-tight truncate">
                Radio Admin
              </h1>
              <p className="text-foreground-400 text-[10px]">Panel de control</p>
            </div>
          )}
          {/* Global actions */}
          <div className="ml-auto flex items-center gap-1">
            <Link
              to="/admin/notifications"
              className="relative w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
              title="Notificaciones"
            >
              <i className="ri-notification-3-line text-foreground-500 text-xs"></i>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-accent-500 border border-background-50 flex items-center justify-center">
                  <span className="text-background-50 text-[7px] font-bold leading-none">{unreadCount > 9 ? '9+' : unreadCount}</span>
                </span>
              )}
            </Link>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
              title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            >
              <i
                className={`${
                  collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'
                } text-foreground-500 text-xs`}
              ></i>
            </button>
          </div>
        </>
      )}
    </div>
  );

  /* ─── Nav footer (user + plan) ─── */
  const renderFooter = () => (
    <div className="px-3 py-4 border-t border-background-200 shrink-0 space-y-3">
      {/* Plan badge */}
      {!collapsed && (
        <div className={`rounded-xl p-2.5 ${
          currentPlan.id === 'free'
            ? 'bg-secondary-50 border border-secondary-200'
            : currentPlan.id === 'pro'
              ? 'bg-primary-50 border border-primary-200'
              : 'bg-accent-50 border border-accent-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                currentPlan.id === 'free'
                  ? 'bg-secondary-200'
                  : currentPlan.id === 'pro'
                    ? 'bg-primary-200'
                    : 'bg-accent-200'
              }`}>
                <i className={`${
                  currentPlan.id === 'free'
                    ? 'ri-vip-line text-secondary-600'
                    : currentPlan.id === 'pro'
                      ? 'ri-star-fill text-primary-600'
                      : 'ri-vip-diamond-fill text-accent-600'
                } text-xs`}></i>
              </div>
              <div>
                <p className={`text-xs font-bold ${
                  currentPlan.id === 'free'
                    ? 'text-secondary-600'
                    : currentPlan.id === 'pro'
                      ? 'text-primary-600'
                      : 'text-accent-600'
                }`}>
                  Plan {currentPlan.name}
                </p>
                <p className="text-foreground-400 text-[9px]">{currentPlan.price}</p>
              </div>
            </div>
            {isFree && (
              <button
                onClick={openUpgradeModal}
                className={`px-2 py-1 rounded-lg text-[9px] font-bold bg-secondary-200 text-secondary-600 hover:bg-secondary-300 cursor-pointer transition-colors whitespace-nowrap`}
              >
                Mejorar
              </button>
            )}
          </div>
        </div>
      )}

      {/* User */}
      <div
        className={`flex items-center gap-3 p-2 rounded-xl bg-background-100 ${
          collapsed ? 'justify-center' : ''
        }`}
      >
        <div className="w-8 h-8 rounded-full bg-primary-100 overflow-hidden shrink-0">
          <img
            src="https://readdy.ai/api/search-image?query=Profile%20photo%20of%20male%20radio%20station%20manager%2C%20professional%20headshot%2C%20warm%20friendly%20expression%2C%20casual%20business%20attire%2C%20clean%20studio%20background%2C%20confident%20approachable%20portrait&width=80&height=80&seq=admin-user-avatar&orientation=squarish"
            alt="Admin"
            className="w-full h-full object-cover"
          />
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-foreground-800 text-xs font-semibold truncate">Carlos Mendoza</p>
            <p className="text-foreground-400 text-[10px] truncate">Administrador</p>
          </div>
        )}
        {!collapsed && (
          <button 
            onClick={() => {
              Swal.fire({
                title: '¿Cerrar sesión?',
                text: 'Vas a salir del panel de administración.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Cerrar sesión',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#9ca3af',
                customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap' },
                buttonsStyling: false,
              });
            }}
            className="w-7 h-7 rounded-lg bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors" title="Cerrar sesión">
            <i className="ri-logout-box-r-line text-foreground-500 text-xs"></i>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* ─── DESKTOP SIDEBAR ─── */}
      <aside
        className={`hidden lg:flex flex-col bg-background-50 border-r border-background-200 h-screen sticky top-0 transition-all duration-300 ${
          collapsed ? 'w-[72px]' : 'w-[240px]'
        }`}
      >
        {renderHeader(false)}
        {renderNavItems(false)}
        {renderFooter()}
      </aside>

      {/* ─── MOBILE HAMBURGER ─── */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-3 left-3 z-50 w-10 h-10 rounded-xl bg-background-50 border border-background-200 shadow-sm flex items-center justify-center cursor-pointer hover:bg-background-100 transition-colors"
        aria-label="Abrir menú"
      >
        <i className="ri-menu-line text-foreground-600 text-lg"></i>
      </button>

      {/* ─── MOBILE DRAWER ─── */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 z-[60] bg-black/40 transition-opacity"
            onClick={() => setMobileOpen(false)}
          ></div>

          {/* Drawer panel */}
          <aside
            className={`lg:hidden fixed inset-y-0 left-0 z-[70] w-[280px] max-w-[85vw] bg-background-50 border-r border-background-200 flex flex-col shadow-xl transform transition-transform duration-300 ${
              mobileOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {renderHeader(true)}
            {renderNavItems(true)}
            {renderFooter()}
          </aside>
        </>
      )}

      {/* ─── Upgrade Modal ─── */}
      <UpgradeModal
        open={upgradeFeatureId !== null}
        featureId={upgradeFeatureId || ''}
        onClose={closeUpgradeModal}
      />
    </>
  );
}